import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionPanel,
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  Grid,
  Inscription,
  Preloader,
  Responsive,
  RoundDoneIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import { DEFAULT_CARD_EXP_DATE_FORMAT } from '@/common/constants';
import { PaymentMethodDetails } from '@/modules/transaction/components/TransactionDetails/TransactionPaymentDetails/components/PaymentMethodDetails/PaymentMethodDetails';
import { useGetPaymentDetails } from '@/modules/transaction/components/TransactionDetails/TransactionPaymentDetails/queries/useGetPaymentDetails.query';
import { PaymentType, Service, TransactionData, TransactionType } from '@/modules/transaction/transaction.types';
import { PaymentMethodType } from '@/modules/user/user.types';
import { formatCurrency } from '@/utils';
import businessDaysPassed from '@/utils/modifiers/businessDaysPassed';
import { phoneNumberCreator } from '@/utils/modifiers/phoneNumberCreator';

import PreviewPaymentDocument from './components/PreviewPaymentDocument/PreviewPaymentDocument';
import { CheckStatus, checkStatusColor, PaymentMethod } from './constants';
import { usePlaidAccountVerificationStatusStore } from './stores';
import { useServicesByIdQuery } from './useServicesByIdQuery.query';
import VoidCheckPopup from './VoidCheckPopup';

const BUSINESS_DAYS = 10;
const EMPTY_TEXT = '-';

const Content = styled(Box)`
  border-radius: ${({ theme }) => theme.rounds.container1Round};
  position: relative;
`;
const IconWrap = styled(Box)`
  ${({ theme }) => theme.fonts.text24Regular}
`;
const DetailsWrap = styled(Box)`
  flex: 1;
`;
const FlexGrid = styled(Grid)`
  flex: 1;
`;

const StatusIcon = ({ paymentStatus }: { paymentStatus: CheckStatus }) => {
  switch (paymentStatus) {
    case CheckStatus.SENT:
      return (
        <Box margin={{ right: 'spacing4' }}>
          <RoundDoneIcon size="20px" color="success" />
        </Box>
      );
    default:
      return (
        <Box
          width={{ width: '10px', min: '10px' }}
          height="10px"
          margin={{ right: 'spacing8' }}
          round
          background={checkStatusColor[paymentStatus as CheckStatus]}
        />
      );
  }
};

interface Props {
  postingId?: string;
  claimId?: string;
  paymentMethodId?: string;
  data?: TransactionData;
  service?: Service;
  employeeId?: string;
  transactionType?: TransactionType;
}

const TransactionPaymentDetails: React.FC<Props> = (props) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [isVisibleNeedHelpPopup, setIsVisibleNeedHelpPopup] = useState(false);
  const { data, paymentDetails, isLoading, refetch, directDepositInfo } = useGetPaymentDetails(props);
  const setPlaidAccountVerificationStatus = usePlaidAccountVerificationStatusStore(
    (state) => state.setPlaidAccountVerificationStatus
  );
  const setAccountId = usePlaidAccountVerificationStatusStore((state) => state.setAccountId);
  const setAdditionalAction = usePlaidAccountVerificationStatusStore((state) => state.setAdditionalAction);
  const actualPayment = useMemo(() => {
    if (paymentDetails?.length) {
      return paymentDetails[paymentDetails.length - 1];
    }
  }, [paymentDetails]);
  const checkId = useMemo(() => actualPayment?.paymentNumber, [actualPayment]);
  const mailingDate = useMemo(() => actualPayment?.mailingDate, [actualPayment]);
  const paymentStatus = useMemo(() => actualPayment?.paymentStatus, [actualPayment]);
  const isResentCheckAvailable = useMemo(() => {
    if (mailingDate) return dayjs(mailingDate).isBefore(businessDaysPassed(BUSINESS_DAYS, dayjs().format()));
    return false;
  }, [mailingDate]);
  const isPaymentMethodTypeCheck = useMemo(
    () => data?.payment_method_type === PaymentMethodType.CHECK,
    [data]
  );
  const hasHelpButton = useMemo(
    () => isPaymentMethodTypeCheck && (paymentStatus === 'ISSUED' || paymentStatus === 'PENDING'),
    [paymentStatus, isPaymentMethodTypeCheck]
  );
  const isDisabledCheckHelpButton = useMemo(
    () => !isPaymentMethodTypeCheck || paymentStatus === 'PENDING',
    [paymentStatus, isPaymentMethodTypeCheck]
  );
  const [activePanels, setActivePanels] = useState<{ [key: number]: number[] }>({
    0: [0],
    1: [0],
  });
  const isPanelActive = (idx: number) => activePanels[idx]?.includes(0);

  useEffect(() => {
    if (directDepositInfo?.status) {
      setPlaidAccountVerificationStatus(directDepositInfo.status);
      setAccountId(directDepositInfo?.accountId);
      setAdditionalAction(refetch);
    }
  }, [directDepositInfo, setPlaidAccountVerificationStatus, setAccountId, setAdditionalAction, refetch]);

  const details = useMemo(
    () => [
      paymentDetails?.filter((item) => item?.invoiceType !== PaymentType.REPAYMENT),
      paymentDetails?.filter((item) => item?.invoiceType === PaymentType.REPAYMENT),
    ],
    [paymentDetails]
  );

  const { isServiceHidden, isLoading: areHiddenServicesLoading } = useServicesByIdQuery(props?.service?.id);

  const isServiceAvailable = useMemo(() => {
    if (!props.transactionType) return true;
    return ![
      TransactionType.OUTBOUND_TRANSFER_OF_ASSETS,
      TransactionType.INBOUND_TRANSFER_OF_ASSETS,
      TransactionType.CLAIM_REPAYMENT,
    ].includes(props.transactionType);
  }, [props?.transactionType])

  if (isLoading || areHiddenServicesLoading) {
    return (
      <Box pad="spacing24">
        <Preloader />
      </Box>
    );
  }

  return (
    details?.map((item, index) => {
      if (!item?.length) return null;

      return (
        <Box data-testId={index ? 'repayment_details' : 'payment_details'} key={index}>
          <Content
            round="regular"
            background="canvas"
            border={{ size: 'small', color: 'border2' }}
            margin={{ top: 'spacing12' }}
          >
            {!index && hasHelpButton && !isServiceHidden && isServiceAvailable ? (
              <Box
                justify="center"
                margin={{ top: 'spacing24' }}
                style={{ position: 'absolute', zIndex: '10', right: '24px' }}
              >
                <Box
                  {...(isDisabledCheckHelpButton
                    ? {
                        style: { cursor: 'not-allowed' },
                      }
                    : {
                        onClick: () => setIsVisibleNeedHelpPopup(true),
                      })}
                >
                  <Inscription color={isDisabledCheckHelpButton ? 'textDisabled' : 'textAccent'}>
                    {isMobile ? t('I need help') : t('I need help with my check')}
                  </Inscription>
                </Box>
                {isVisibleNeedHelpPopup ? (
                  <VoidCheckPopup
                    id={checkId}
                    paymentMethodId={props?.paymentMethodId}
                    isVisible
                    close={() => setIsVisibleNeedHelpPopup(false)}
                    refetch={refetch}
                    isResentCheckAvailable={isResentCheckAvailable}
                    mailingDate={mailingDate}
                  />
                ) : null}
              </Box>
            ) : null}

            <Accordion
              pad="spacing24"
              activeIndex={activePanels[index]}
              onActive={(val) => setActivePanels({ ...activePanels, [index]: val })}
            >
              <AccordionPanel
                header={
                  <Box direction="row" align="center" justify="between">
                    <Box direction="row" align="center">
                      <IconWrap pad={{ right: 'spacing8' }}>
                        {isPanelActive(index) ? (
                          <ChevronUpIcon size="20px" color="iconPrimary" />
                        ) : (
                          <ChevronDownIcon size="20px" color="iconPrimary" />
                        )}
                      </IconWrap>
                      <Text size="large" weight="normal">
                        {index ? 'Repayment Details' : 'Payment Details'}
                      </Text>
                    </Box>
                  </Box>
                }
              >
                {item?.map((details, index) => (
                  <Box key={details.id} direction="column">
                    <Box direction={isMobile ? 'column' : 'row'} pad={{ top: 'spacing24' }}>
                      <Box>
                        <Inscription color="textSecondary" lineHeight="20px" margin={{ bottom: 'spacing12' }}>
                          Payment # {index + 1}
                        </Inscription>
                        <PaymentMethodDetails
                          method={
                            {
                              type: details.paymentMethodType,
                              ...(details.paymentMethodType === PaymentMethodType.DIRECT_DEPOSIT
                                ? {
                                    accountNumber: Array.from(
                                      new Array(details?.requisites?.accountNumberLength),
                                      () => '*'
                                    ).join(''),
                                    accountDescription: details?.requisites?.accountDescription,
                                    accountNumberLast4: details?.requisites?.accountNumberLast4,
                                  }
                                : {
                                    requisites: {
                                      ...details.requisites,
                                      phoneNumber: details?.requisites?.phoneNumber
                                        ? phoneNumberCreator(details?.requisites?.phoneNumber)
                                        : '',
                                    },
                                    address: details?.paymentAddress,
                                  }),
                              isVerified: true,
                            } as Partial<PaymentMethod>
                          }
                          checkStatus={details?.paymentStatus as CheckStatus}
                        />
                      </Box>
                      <DetailsWrap margin={isMobile ? { top: 'spacing12' } : {}}>
                        <FlexGrid
                          rows={['auto', 'auto']}
                          columns={['auto', 'auto']}
                          gap="small"
                          areas={[
                            { name: 'grid-1', start: [0, 0], end: [0, 1] },
                            { name: 'grid-2', start: [1, 0], end: [1, 1] },
                          ]}
                        >
                          <Box gridArea="grid-1">
                            <Box margin={{ bottom: 'spacing24' }}>
                              <Text color="textSecondary" size="small" weight={500}>
                                Amount
                              </Text>
                              <Text
                                margin={{ top: 'spacing2' }}
                                color="textBody"
                                weight="bold"
                                data-testId="payment_details_amount"
                              >
                                {formatCurrency(details?.amount)}
                              </Text>
                            </Box>
                            {details.paymentMethodType === PaymentMethodType.DIRECT_DEPOSIT ? (
                              <Box margin={{ bottom: 'spacing12' }}>
                                <Box margin={{ bottom: 'spacing12' }}>
                                  <Text color="textSecondary" size="small" weight={500}>
                                    Trace Number
                                  </Text>
                                  <Text
                                    margin={{ top: 'spacing2' }}
                                    color="textBody"
                                    data-testId="payment_details_trace_id"
                                  >
                                    {details?.traceId || EMPTY_TEXT}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text color="textSecondary" size="small" weight={500}>
                                    Invoice Number
                                  </Text>
                                  <Text
                                    margin={{ top: 'spacing2' }}
                                    color="textBody"
                                    data-testId="payment_details_invoice_id"
                                  >
                                    {details?.invoiceId || EMPTY_TEXT}
                                  </Text>
                                </Box>
                              </Box>
                            ) : (
                              <Box margin={{ bottom: 'spacing24' }}>
                                <Text color="textSecondary" size="small" weight={500}>
                                  Payment Number
                                </Text>
                                <Text
                                  margin={{ top: 'spacing2' }}
                                  color="textBody"
                                  data-testId="payment_details_payment_number"
                                >
                                  {details?.paymentNumber || EMPTY_TEXT}
                                </Text>
                              </Box>
                            )}
                            <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
                              <Box>
                                <Text color="textSecondary" size="small" weight={500}>
                                  Pay To
                                </Text>
                                <Text
                                  margin={{ top: 'spacing2' }}
                                  color="textBody"
                                  data-testId="payment_details_pay_to"
                                >
                                  {details.payTo}
                                </Text>
                              </Box>
                            </Responsive>
                          </Box>
                          <Box gridArea="grid-2">
                            <Box margin={{ bottom: 'spacing24' }}>
                              <Text color="textSecondary" size="small" weight={500}>
                                Payment Status
                              </Text>
                              <Box margin={{ top: 'spacing2' }} direction="row" align="center">
                                <StatusIcon paymentStatus={details?.paymentStatus as CheckStatus} />
                                <Text color="textBody" data-testId="payment_details_payment_status">
                                  {capitalize(details?.paymentStatus)}
                                </Text>
                              </Box>
                            </Box>
                            <Box margin={isMobile ? { bottom: 'spacing12' } : { bottom: 'spacing24' }}>
                              <Text color="textSecondary" size="small" weight={500}>
                                Payment Date
                              </Text>
                              <Text
                                margin={{ top: 'spacing2' }}
                                color="textBody"
                                data-testId="payment_details_payment_date"
                              >
                                {details?.paymentDate
                                  ? dayjs(details?.paymentDate).format(DEFAULT_CARD_EXP_DATE_FORMAT)
                                  : EMPTY_TEXT}
                              </Text>
                            </Box>
                            <Responsive size={Responsive.Mobile}>
                              <Box>
                                <Text color="textSecondary" size="small" weight={500}>
                                  Pay To
                                </Text>
                                <Text
                                  margin={{ top: 'spacing2' }}
                                  color="textBody"
                                  data-testId="payment_details_pay_to"
                                >
                                  {details.payTo}
                                </Text>
                              </Box>
                            </Responsive>
                          </Box>
                        </FlexGrid>
                      </DetailsWrap>
                    </Box>
                    {details?.paymentDocumentPath && (
                      <Box margin={{ top: 'spacing24' }} pad="spacing16" background="module" round="container1Round">
                        <PreviewPaymentDocument paymentDocumentPath={details?.paymentDocumentPath} />
                      </Box>
                    )}
                  </Box>
                ))}
              </AccordionPanel>
            </Accordion>
          </Content>
        </Box>
      );
    }) || null
  );
};

export default TransactionPaymentDetails;
